import React, { useEffect, useMemo } from "react";
import i18n from "i18next";
import PropTypes from "prop-types";
import { LOCAL_FONTS, generateGoogleFontsLink } from "../../../../common/font_extractor.es6";
import "./_fontPickerGrid.scss";

const SYSTEM_FONTS = [
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Times New Roman", value: "Times New Roman, Times, serif" },
  { label: "Courier New", value: "Courier New, Courier, monospace" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
];

// Same grouping the (now-hidden) CKEditor font dropdown used — see the
// font_names comments in dist/lib/ckeditor/plugins/font/plugin.js. Any
// SUPPORTED_GOOGLE_FONTS entry not listed here falls into "Diğer" below
// automatically, so a future addition to that list is never silently hidden.
const GOOGLE_FONT_CATEGORIES = [
  { label: "Sans-Serif", fonts: ["Montserrat", "Lato", "Open Sans", "Roboto", "PT Sans", "Rubik"] },
  {
    label: "Geometrik",
    fonts: [
      "Poppins",
      "DM Sans",
      "Plus Jakarta Sans",
      "Manrope",
      "Outfit",
      "Sora",
      "Jost",
      "Urbanist",
    ],
  },
  { label: "Humanist", fonts: ["Nunito", "Nunito Sans", "Karla", "Cabin", "Work Sans"] },
  {
    label: "Display",
    fonts: [
      "Raleway",
      "Oswald",
      "Josefin Sans",
      "Barlow",
      "Barlow Condensed",
      "Quicksand",
      "Bricolage Grotesque",
    ],
  },
  {
    label: "Tech / UI",
    fonts: [
      "Fira Sans",
      "Source Sans 3",
      "Exo 2",
      "Titillium Web",
      "Ubuntu",
      "Inter",
      "Instrument Sans",
    ],
  },
  { label: "Modern / Minimal", fonts: ["Be Vietnam Pro", "Mulish"] },
  { label: i18n.t("FontCategory.Accessible") || "Erişilebilir", fonts: ["Lexend", "Lexend Deca"] },
  {
    label: "Serif",
    fonts: [
      "Playfair Display",
      "Merriweather",
      "Libre Baskerville",
      "Crimson Text",
      "Cormorant Garamond",
      "Vollkorn",
      "EB Garamond",
      "Spectral",
    ],
  },
  { label: "Slab Serif", fonts: ["Roboto Slab", "Arvo", "Bitter"] },
  { label: i18n.t("FontCategory.Handwriting") || "El Yazısı", fonts: ["Kalam"] },
];

// Builds the full category list once: system fonts, each Google category (only
// the fonts actually present in `googleFonts`), any leftover Google fonts under
// "Diğer", then local (TTF) fonts under "Özel".
function buildSections(googleFonts, localFonts) {
  const remaining = new Set(googleFonts);
  const sections = [{ label: i18n.t("FontCategory.System") || "Sistem", options: SYSTEM_FONTS }];

  GOOGLE_FONT_CATEGORIES.forEach(({ label, fonts }) => {
    const options = fonts
      .filter((f) => remaining.has(f))
      .map((f) => {
        remaining.delete(f);
        return { label: f, value: `${f}, sans-serif` };
      });
    if (options.length) sections.push({ label, options });
  });

  if (remaining.size) {
    sections.push({
      label: i18n.t("FontCategory.Other") || "Diğer",
      options: Array.from(remaining).map((f) => ({ label: f, value: `${f}, sans-serif` })),
    });
  }

  if (localFonts.length) {
    sections.push({
      label: i18n.t("FontCategory.Custom") || "Özel",
      options: localFonts.map((f) => ({ label: f, value: `${f}, sans-serif` })),
    });
  }

  return sections;
}

// Inline, scrollable font list — no modal chrome. Meant to replace a popover's
// own content in place (see TopActionPill/CanvasAlignToolbar's "font-picker"
// row), per the app's "no popup over a popup" rule: this never opens on top of
// anything, it just becomes what the existing popover is currently showing.
//
// Hovering/focusing a row calls `onPreview(value)` so the caller can apply the
// font to the live box(es) immediately, without committing it; leaving the row
// calls `onPreview(null)` to signal "show the committed value again". Clicking
// a row calls `onSelect(value)` to actually commit it.
export default function FontPickerGrid({ value, googleFonts, onSelect, onPreview }) {
  const sections = useMemo(() => buildSections(googleFonts, LOCAL_FONTS), [googleFonts]);

  // Load every Google font shown here in one batched stylesheet link, so each
  // row actually renders in its own typeface instead of a fallback. Skips
  // fonts already loaded elsewhere (same dedup check CKEDitorComponent.jsx
  // uses for its own auto-loaded fonts).
  useEffect(() => {
    const fontsMap = new Map();
    googleFonts.forEach((f) => fontsMap.set(f, new Set([400, 600, 700])));
    const linkHTML = generateGoogleFontsLink(fontsMap);
    const href = linkHTML.match(/href=["']([^"']+)["']/)?.[1];
    if (!href) return;
    const alreadyLoaded = Array.from(
      document.querySelectorAll('link[href*="fonts.googleapis.com"]')
    ).some((l) => l.href === href);
    if (alreadyLoaded) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-font-picker", "true");
    document.head.appendChild(link);
  }, [googleFonts]);

  const preview = (v) => onPreview && onPreview(v);

  return (
    <div className="font-picker">
      {sections.map((section) => (
        <div className="font-picker__section" key={section.label}>
          <div className="font-picker__section-title">{section.label}</div>
          <div className="font-picker__list">
            {section.options.map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={
                  "font-picker__item" + (opt.value === value ? " font-picker__item--active" : "")
                }
                style={{ fontFamily: opt.value }}
                onMouseEnter={() => preview(opt.value)}
                onMouseLeave={() => preview(null)}
                onFocus={() => preview(opt.value)}
                onBlur={() => preview(null)}
                onClick={() => onSelect(opt.value)}
                title={opt.label}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

FontPickerGrid.propTypes = {
  value: PropTypes.string,
  googleFonts: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
};
